import React from "react";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link } from "react-router-dom";
import { DndContext, PointerSensor, useSensor, useSensors, DragOverlay, closestCenter } from '@dnd-kit/core';
import { SortableContext, useSortable, horizontalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

function LanguageItem({ language, isDefault, onDelete }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: language,
        disabled: isDefault
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.3 : 1,
        zIndex: isDragging ? 0 : 'auto'
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={'language' + (isDefault ? ' language--default' : '')}
            {...attributes}
        >
            <p>
                {!isDefault && (
                    <span {...listeners} className="language-sort-icon">
                        <FontAwesomeIcon icon={['fas', 'grip-vertical']} />
                    </span>
                )}
                {language}
                {!isDefault && (
                    <Link to="#" onClick={onDelete}><FontAwesomeIcon icon={['fas', 'xmark']} /></Link>
                )}
            </p>
        </div>
    );
}

function LanguageSortableList({ languages, defaultLanguage, onDelete, onSortEnd }) {
    const [activeId, setActiveId] = React.useState(null);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
    );

    const handleDragEnd = ({ active, over }) => {
        setActiveId(null);
        if (!over || active.id === over.id) return;
        const oldIndex = languages.indexOf(active.id);
        const newIndex = languages.indexOf(over.id);
        if (newIndex === 0) return;
        onSortEnd(arrayMove(languages, oldIndex, newIndex));
    };

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={({ active }) => setActiveId(active.id)}
            onDragEnd={handleDragEnd}
            onDragCancel={() => setActiveId(null)}
        >
            <SortableContext items={languages} strategy={horizontalListSortingStrategy}>
                <div className="languages-sortable">
                    {languages.map((language) => (
                        <LanguageItem
                            key={language}
                            language={language}
                            isDefault={language === defaultLanguage}
                            onDelete={(e) => onDelete(e, language)}
                        />
                    ))}
                </div>
            </SortableContext>
            <DragOverlay>
                {activeId ? (
                    <div className="language language--dragging">
                        <p>
                            <span className="language-sort-icon">
                                <FontAwesomeIcon icon={['fas', 'grip-vertical']} />
                            </span>
                            {activeId}
                        </p>
                    </div>
                ) : null}
            </DragOverlay>
        </DndContext>
    );
}

LanguageSortableList.propTypes = {
    languages: PropTypes.array.isRequired,
    defaultLanguage: PropTypes.string,
    onDelete: PropTypes.func.isRequired,
    onSortEnd: PropTypes.func.isRequired
};
export default LanguageSortableList;
